import { Suspense, useCallback, useContext, useEffect, useRef, useState } from "react";
import { flush, track } from "../track";
import Sheet from "../ui/Sheet";
import { checkoutResult, PageContext, type BaseChoice, type CheckoutResult } from "./page";
import ChristmasBase, { baseBlocks, chosenBase } from "./ChristmasBase";
import { baseKindOf } from "./christmas";
import ExpressChoice, { ExpressHold, useExpress, type ExpressTotal } from "./ExpressChoice";
import { LazyPayForm, reloadToPay } from "./OrderSheet";
import type { PayFailure, PayLine } from "./PayForm";
import { hasCode, type BaseStyle, type LeadKind, type PreviewDoc, type PreviewPiece } from "../types";
import type { Words } from "./copy";
import { KIND_KEY, openingSize } from "./kinds";
import { LateLine, expressNote } from "./Facts";
import { pieceKey, qtySel } from "./selection";
import type { OtherPreview } from "./otherPreviews";
import { bill, fill, MAX_UNITS } from "./multiBuy";
import { billIn, BillBox, billRows, MultiBuyOffer, multiBuyWords, offVars, ownsQuantity, QtyStepper, unbroken } from "./Quantity";

/** A piece as this sheet offers it: the piece, where it sits on its page
 *  ("Piece 2 of 3"), the drawing it is shown by, and which preview it is of:
 *  this page's own (`token` ""), or another of theirs, from the strip of
 *  their previews (otherPreviews). */
export interface TogetherPiece {
  piece: PreviewPiece;
  at: number;
  /** How many pieces its own preview has, for "Piece 2 of 3". */
  of: number;
  drawingUrl: string | null;
  token: string;
}

/** What a tap on a suggestion's "Add ... too" asks the sheet for (Suggestions.tsx):
 *  `n` counts the taps, so a second tap after the sheet was closed opens it
 *  again, and `pieces` are the ones to open it with, each at the size the
 *  page has for it, or null for its opening size. Everything else on the page
 *  is left out of it, to be ticked by whoever wants it. */
export interface TogetherAsk {
  n: number;
  pieces: { id: string; size: string | null }[];
}

/** A piece's key in the sheet: ids are a lead's own, and every preview of
 *  one piece calls its piece "", so another preview's could be this one's. */
function keyOf({ token, piece }: { token: string; piece: PreviewPiece }): string {
  return pieceKey(token, piece.id);
}

/** "Order them together", under the pieces on a page of several.
 *
 *  Each card already offers its own piece on its own, which is right when the
 *  pieces are alternatives (each of two people, or the two together). But three
 *  dogs are three keychains, and someone who wants all three used to pay for
 *  one and find the page closed to the other two: one payment is one order,
 *  and a page that has been paid for offers nothing more. This is the same one
 *  payment with every piece they want in it, at a size each, and how many of
 *  each they choose in Stripe's form; or, where the multi-buy applies (every
 *  extra piece in the same parcel cheaper, preview/multiBuy.ts), beside each
 *  piece here, under a bill that says what the parcel comes to.
 *
 *  And with the strip of their other previews (Others.tsx), the pieces of
 *  those too, where paying for several previews at once is offered: someone
 *  who asked for a figurine of their mother and a keychain of the dog pays
 *  for both here, once, rather than on two pages. Each keeps its own price
 *  and its own code, and each becomes a sale of its own preview.
 *
 *  Only where the page can take the payment itself. Etsy has no basket for
 *  pieces drawn from someone's photos, so a page without "Pay here" offers
 *  each piece on its own card and nothing here. */
export default function OrderAll({
  pieces,
  others,
  fallbackKind,
  words: t,
  autoOpen,
  ask,
  basket,
  quiet,
  add,
  delivery,
}: {
  pieces: TogetherPiece[];
  /** Their other previews, from the strip, with what of each can be paid for
   *  here. */
  others?: OtherPreview[] | null;
  /** The lead's product, for a piece written before pieces carried their own. */
  fallbackKind: LeadKind;
  words: Words;
  /** Came back to the page to pay for these ("pay=1&piece=all"), after a
   *  sheet that could not load its form asked for a reload. */
  autoOpen?: boolean;
  /** Counts the taps on "Choose your pieces" under "Love it", up from 0: each
   *  opens the sheet where this offers one, and otherwise takes them to the
   *  first card that has its own way to order. */
  ask?: number;
  /** The page lets them choose on its cards (selection.ts), and says what
   *  the order comes to under them and in the bar: this card, which offered
   *  the same thing without a choice, is left out. */
  basket?: boolean;
  /** No offer card of its own either: the page shows one piece of several at a
   *  time, and this sheet opens from the "Add ... too" of a suggestion
   *  (`add`) alone. Still mounted, since the sheet and its checkout are this
   *  component's. */
  quiet?: boolean;
  /** A tap on a suggestion's "Add ... too": the sheet opens with the page's
   *  piece and that one chosen, in place of everything. */
  add?: TogetherAsk | null;
  /** The page's delivery lines, for the thank-you's word about the
   *  printable card, and said on the sheet's first step. */
  delivery?: PreviewDoc["delivery"];
}) {
  const page = useContext(PageContext);
  const [open, setOpen] = useState(false);
  /** The suggestion the open sheet was made for, if it was opened by one. */
  const [adding, setAdding] = useState<TogetherAsk | null>(null);
  const close = useCallback(() => {
    setOpen(false);
    setAdding(null);
  }, []);
  const opened = useRef(false);
  // Only the pieces that can be paid for here: priced, with their sizes. This
  // page's own, then each other preview's, oldest first, as the strip shows
  // them.
  const own = pieces.filter((p) => !!p.piece.price?.sizes?.length);
  const theirs = othersOf(others);
  const payable = own.length ? [...own, ...theirs] : [];
  // Their previews that could have been here and cannot: said in the sheet,
  // so a preview missing from it is not a mystery.
  const notHere = (others ?? []).filter((o) => !o.current && o.notHere).length;
  useEffect(() => {
    if (!autoOpen || opened.current || payable.length < 2) return;
    opened.current = true;
    setOpen(true);
  }, [autoOpen, payable.length]);
  // Offered only with two pieces or more to pay for here. A sheet already
  // open stays, though, whatever the page reads meanwhile: the one that has
  // just taken a payment is saying thank you, and a re-read after it finds
  // the other previews ordered and this page no longer taking payment.
  const offered = !!page?.checkout && payable.length >= 2;
  const asked = useRef(ask ?? 0);
  useEffect(() => {
    if (!ask || ask === asked.current) return;
    asked.current = ask;
    if (offered) setOpen(true);
    else document.querySelector(".offer")?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [ask, offered]);
  // A suggestion's "Add ... too": the taps already there when this mounted
  // are not new ones. Where nothing is offered (a payment this page no longer
  // takes) the suggestion has its own "See this one" and never gets here.
  const addSeen = useRef(add?.n ?? 0);
  useEffect(() => {
    if (!add || add.n === addSeen.current) return;
    addSeen.current = add.n;
    if (!offered) return;
    setAdding(add);
    setOpen(true);
  }, [add?.n, offered]);
  if (!offered && !open) return null;
  // Paid for, or being paid: as with every other order button, the sheet that
  // took the payment stays to say thank you, and the button steps aside.
  const done = !!page?.ordered && !page?.admin;
  // The reason to pay for them in one go, where there is one: every extra
  // piece in the same parcel is cheaper (the multi-buy). Said when any piece
  // here would take something off; a code that already takes more says
  // nothing about it.
  // Inside a dated window, "habitualmente −25%" only when it is true of every
  // piece here: a FIRSTORDER30 preview's pieces are sent the window with no
  // `usual`, because theirs is usually their own 30%.
  const offers = payable.map((p) => p.piece.price?.multiBuy).filter((m) => !!m);
  const found = offers[0] ?? null;
  const multi =
    found?.endsOn && offers.some((m) => m.usual == null) ? { ...found, usual: undefined } : found;
  const language = page?.language ?? "en";
  const multiSaid = multi ? multiBuyWords(multi, t, language) : null;

  return (
    <>
      {offered && !done && !basket && !quiet && (
        <div className="card together">
          <p className="small" style={{ margin: 0 }}>
            {t.t(theirs.length ? "togetherNoteOthers" : "togetherNote")}
          </p>
          {/* Under a sentence that already says to order them together, so
              the offer says only what that is worth: the price of each
              extra piece, and one parcel. */}
          {multi && multiSaid && (
            <MultiBuyOffer badge={t.t("multiBuyOff", offVars(multi.percent))}>
              {/* Inside a dated window, under the framing's title, with
                  "habitualmente −25%" and its last day (multiBuyWords). */}
              {multiSaid.dated && (
                <>
                  <strong>{multiSaid.title}</strong>
                  {/* Chinese runs on with no space. */}
                  {language === "zh" || language === "zh-hans" ? "" : " "}
                </>
              )}
              {multiSaid.together}
            </MultiBuyOffer>
          )}
          <button className="btn btn-primary btn-block btn-lift" onClick={() => setOpen(true)}>
            {t.t("togetherButton")}
          </button>
        </div>
      )}
      {open && (
        <TogetherSheet
          pieces={payable}
          notHere={notHere}
          fallbackKind={fallbackKind}
          words={t}
          card={!!delivery?.late && !!delivery.card}
          delivery={delivery}
          start={adding}
          onClose={close}
        />
      )}
    </>
  );
}

/** What each piece is set to: a size, or null for one left out. */
type Picks = Record<string, string | null>;

type Offer = NonNullable<PreviewPiece["price"]>;
type Chip = NonNullable<Offer["sizes"]>[number];
/** A piece in the payment, at the size and the price its chip showed, on its
 *  Christmas base where it has one. */
type Chosen = TogetherPiece & {
  kind: LeadKind;
  size: string;
  chip: Chip;
  offer: Offer;
  base: { base: BaseStyle; message: string } | null;
};
/** What a payment is for, as it was when they pressed Continue. Held, so that
 *  the page reading itself or their other previews again while the form is
 *  up (back from the bank's app, say) cannot change what the form charges
 *  for under them: a different list would be a new checkout. */
interface Payment {
  chosen: Chosen[];
  lines: PayLine[];
  total: string | null;
  vatNote?: string;
  withCode: boolean;
  previews: number;
  /** Whether the sheet said how many and worked the bill out (the
   *  multi-buy): then `total` is sent with the lines and checked by the
   *  server, and each line's `amounts` and `qtys` are what the summary says.
   *  Otherwise Stripe's form says how many, as it always did. */
  owned: boolean;
  qtys: number[];
  amounts: string[];
  /** What the multi-buy took off the whole payment, in minor units. */
  off: number;
  /** What the payment comes to, in minor units and the format it is written
   *  in, for the total with express. Null where the chips do not say it. */
  sum: ExpressTotal | null;
}

/** The most pieces, and previews, one payment takes: the server's
 *  MAX_LINES_TOGETHER and MAX_PREVIEWS_TOGETHER (functions/src/lib/together.ts). */
const MAX_LINES = 10;
const MAX_PREVIEWS = 5;

function TogetherSheet({
  pieces,
  notHere,
  fallbackKind,
  words: t,
  card,
  delivery,
  start,
  onClose,
}: {
  pieces: TogetherPiece[];
  /** How many of their other previews cannot be added here. */
  notHere: number;
  fallbackKind: LeadKind;
  words: Words;
  /** A payment now gets the printable card (the page's delivery.card, in
   *  the weeks Christmas is no longer promised): the thank-you says so. */
  card: boolean;
  /** When it arrives and what is included, said under the list: what they
   *  are about to commit to, before the button that does. */
  delivery?: PreviewDoc["delivery"];
  /** Opened by a suggestion's "Add ... too": the pieces to open it with. */
  start?: TogetherAsk | null;
  onClose: () => void;
}) {
  const page = useContext(PageContext);
  // Opened from a suggestion: the two pieces it named, each at the size the
  // page has for it, and nothing else, whatever was chosen in an earlier
  // sheet. On a page that lets them choose (selection.ts), what they chose on
  // the cards, and nothing of their other previews: they pressed Continue on an
  // order, not on a request to buy everything. Otherwise every piece in, at
  // the size its own sheet would open on: they pressed "together", so the
  // likeliest answer is all of them. As many as one payment takes, that is:
  // this page's first, then their others' in order.
  const [picks, setPicks] = useState<Picks>(() => {
    const out: Picks = {};
    if (start) {
      for (const p of pieces) {
        const asked = p.token ? undefined : start.pieces.find((s) => s.id === p.piece.id);
        const sizes = p.piece.price?.sizes ?? [];
        out[keyOf(p)] = !asked
          ? null
          : asked.size && sizes.some((s) => s.size === asked.size)
            ? asked.size
            : openingSize(sizes, p.piece.kind ?? fallbackKind);
      }
      return out;
    }
    const chosenOnPage = page?.picks;
    if (chosenOnPage && Object.values(chosenOnPage).some(Boolean)) {
      for (const p of pieces) {
        const chosenSize = p.token ? null : (chosenOnPage[keyOf(p)] ?? null);
        const sizes = p.piece.price?.sizes ?? [];
        out[keyOf(p)] = chosenSize && sizes.some((s) => s.size === chosenSize) ? chosenSize : null;
      }
      return out;
    }
    const tokens = new Set<string>();
    for (const p of pieces) {
      const fits = Object.values(out).filter(Boolean).length < MAX_LINES &&
        (tokens.has(p.token) || tokens.size < MAX_PREVIEWS);
      out[keyOf(p)] = fits ? openingSize(p.piece.price?.sizes ?? [], p.piece.kind ?? fallbackKind) : null;
      if (fits) tokens.add(p.token);
    }
    return out;
  });
  // The page read itself again and a piece's sizes changed under the sheet:
  // a size it no longer has would be refused on every try, so it is picked
  // again. A piece that is new to the page comes in left out.
  const sizesKey = pieces.map((p) => `${keyOf(p)}:${(p.piece.price?.sizes ?? []).map((s) => s.size).join("|")}`).join(";");
  const picksNow = useRef(picks);
  picksNow.current = picks;
  useEffect(() => {
    const before = picksNow.current;
    const next: Picks = {};
    for (const p of pieces) {
      const sizes = p.piece.price?.sizes ?? [];
      const had = before[keyOf(p)];
      next[keyOf(p)] = had === undefined ? null : had === null ? null : sizes.some((s) => s.size === had) ? had : openingSize(sizes, p.piece.kind ?? fallbackKind);
      // The page's choice follows, or the bar behind the sheet would drop
      // the piece the sheet still lists.
      if (!p.token && had && next[keyOf(p)] !== had) page?.onPick?.(keyOf(p), next[keyOf(p)] ?? null);
    }
    setPicks(next);
  }, [sizesKey]);

  // How many of each ticked piece, where the sheet says how many (the
  // multi-buy): one unless they pressed +, and back to one when the piece is
  // left out. Not remembered on the device.
  // On a page that lets them choose, the numbers they set on the cards.
  const [qtys, setQtys] = useState<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    for (const p of pieces) {
      const kept = p.token ? undefined : page?.qtys?.[qtySel(keyOf(p))];
      if (kept && kept > 1) out[keyOf(p)] = kept;
    }
    return out;
  });
  // What they change here is what the cards show when it closes: this page's
  // own pieces, written back to the selection.
  const onPick = page?.onPick;
  const onQty = page?.onQty;
  function choose(p: TogetherPiece, size: string | null) {
    setPicks((before) => ({ ...before, [keyOf(p)]: size }));
    if (!p.token) onPick?.(keyOf(p), size);
  }
  function count(p: TogetherPiece, n: number) {
    setQtys((before) => ({ ...before, [keyOf(p)]: n }));
    if (!p.token) onQty?.(qtySel(keyOf(p)), n);
  }
  // The Christmas base of each figure, where the page offers it: switched on
  // and its message typed beside the piece's sizes, under the key its card
  // keeps it by (PageInfo.bases), so the card, the summary and this sheet
  // all say the same. Their other previews' pieces keep theirs here too,
  // under keys of their own.
  const baseLimits = page?.checkout ? (page.baseLimits ?? null) : null;
  const onBase = page?.onBase;
  const [bases, setBases] = useState<Record<string, BaseChoice>>(() => ({ ...(page?.bases ?? {}) }));
  function setBase(p: TogetherPiece, next: BaseChoice) {
    const key = qtySel(keyOf(p));
    setBases((before) => ({ ...before, [key]: next }));
    onBase?.(key, next);
  }

  const [view, setView] = useState<"pick" | "pay" | "paid">("pick");
  const [failed, setFailed] = useState<PayFailure | null>(null);
  /** Back to picking because one of their other previews changed under the
   *  sheet: said above the list until they move on. */
  const [changed, setChanged] = useState(false);
  /** The same because this page's own price changed under the form. */
  const [repriced, setRepriced] = useState(false);
  /** The same because a Christmas base or its message was refused. */
  const [baseRefused, setBaseRefused] = useState(false);
  const previews = new Set(pieces.map((p) => p.token)).size;
  const [closed, setClosed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [outcome, setOutcome] = useState<"checking" | "paid" | "processing">("checking");

  // The same account of the sheet as the one-piece sheet gives, so the
  // numbers page sees these too: when the form showed, and how the sheet went.
  const shownAt = useRef<number | null>(null);
  const viewNow = useRef(view);
  viewNow.current = view;
  const formShown = useCallback(() => {
    if (shownAt.current !== null) return;
    shownAt.current = Date.now();
    const count = payingNow.current?.previews ?? 1;
    track("checkout.loaded", { together: true, ...(count > 1 ? { previews: count } : {}) });
  }, []);
  useEffect(() => {
    const opened = Date.now();
    const gone = (how: "closed" | "hidden") => {
      const at = shownAt.current;
      track("sheet.closed", {
        view: viewNow.current,
        how,
        together: true,
        ms: Date.now() - opened,
        loaded: at !== null,
        ...(at !== null ? { formMs: Date.now() - at } : {}),
      });
      flush();
    };
    const onHide = () => {
      if (document.visibilityState === "hidden") gone("hidden");
    };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      gone("closed");
    };
  }, []);

  // What is in the payment, in the order the page shows the pieces.
  const chosen: Chosen[] = pieces.flatMap((p) => {
    const { piece } = p;
    const size = picks[keyOf(p)];
    const offer = piece.price;
    const chip = size ? offer?.sizes?.find((s) => s.size === size) : undefined;
    if (!size || !offer || !chip) return [];
    const kind = piece.kind ?? fallbackKind;
    return [{ ...p, kind, size, chip, offer, base: chosenBase(bases[qtySel(keyOf(p))], baseKindOf(kind, offer), size, baseLimits) }];
  });
  // A base whose message does not fit, or cannot be printed, keeps Continue
  // off, as it does in the sheet of one piece: never cut, never sent.
  const baseStuck = chosen.some((c) => baseBlocks(bases[qtySel(keyOf(c))], baseKindOf(c.kind, c.offer), c.size, baseLimits));
  // Whether the sheet says how many and works the bill out: where it takes
  // the payment itself, and every piece in it came with what the bill is made
  // of, in one format (the multi-buy, preview/multiBuy.ts). Otherwise the
  // list, the lines and the total are exactly what they always were, and
  // Stripe's form says how many.
  const format = chosen[0]?.offer.format ?? "";
  const owns =
    !!page?.checkout && chosen.length > 0 && chosen.every((c) => ownsQuantity(c.offer) && c.offer.format === format);
  const qtyOf = (p: TogetherPiece) => (owns ? Math.min(Math.max(1, qtys[keyOf(p)] ?? 1), MAX_UNITS) : 1);
  const units = chosen.reduce((sum, c) => sum + qtyOf(c), 0);
  const lines: PayLine[] = chosen.map((c) => ({
    ...(c.token ? { token: c.token } : {}),
    pieceId: c.piece.id,
    kind: c.kind,
    size: c.size,
    shown: c.chip.now,
    ...(owns ? { qty: qtyOf(c) } : {}),
    ...(c.base ? { base: c.base.base, message: c.base.message } : {}),
  }));
  // The bill, in the order the lines are sent: the same sum the server makes
  // before it opens the checkout, and refuses if its total is not this one.
  const billed = owns ? bill(chosen.map((c) => billIn(c.chip, qtyOf(c)))) : null;
  // The payment is filed under the page that takes it, which thanks them for
  // it: at least one of this page's own pieces, or it is another page's to
  // take.
  const ownChosen = chosen.some((c) => !c.token);
  // What one payment takes (MAX_LINES_TOGETHER and MAX_PREVIEWS_TOGETHER in
  // functions/src/lib/together.ts): a piece that would go past either is not
  // offered to tick, and the sheet says why.
  // And where the sheet says how many, no more pieces in all than one payment
  // takes (MAX_UNITS, the server's too): a piece is one more of them.
  const chosenPreviews = new Set(chosen.map((c) => c.token));
  const fits = (p: TogetherPiece) =>
    chosen.length < MAX_LINES &&
    (chosenPreviews.has(p.token) || chosenPreviews.size < MAX_PREVIEWS) &&
    (!owns || units < MAX_UNITS);
  // More in all than one payment takes: the server would refuse it with a
  // plain "try again", so Continue stays off and the sheet says why.
  const overCap = owns && units > MAX_UNITS;
  const atLimit = pieces.some((p) => !picks[keyOf(p)] && !fits(p));
  const total = billed
    ? fill(format, billed.total)
    : totalOf(chosen.map(({ chip, offer }) => ({ cents: chip.cents, format: offer.format })));
  const vatNote = chosen[0]?.offer.vatNote ?? pieces[0]?.piece.price?.vatNote;
  // Every piece on a page carries the lead's one code, or none of them does;
  // another preview of theirs may hold one where this one does not, and the
  // note says a code was taken off when any line had one.
  const codeOn = (c: Chosen) => hasCode(c.offer.code ?? (c.token ? "" : page?.promoCode), c.offer);
  const withCode = chosen.some(codeOn);

  // The payment being made, from Continue until they go back to change it.
  const [paying, setPaying] = useState<Payment | null>(null);
  const payingNow = useRef(paying);
  payingNow.current = paying;
  // Express to a typed address, priced for the sizes being paid for and every
  // piece in the parcel (where the page does not say how many, one of each).
  const express = useExpress(
    delivery,
    paying ? paying.lines.map((l) => l.size) : [],
    paying ? paying.qtys.reduce((sum, n) => sum + n, 0) : 1
  );
  // The window the thank-you says when the payment was express (the express
  // one, counted from today, the day it is paid), kept here and told to the
  // page so neither waits for the page to be read again.
  const [paidArrives, setPaidArrives] = useState("");
  const expressArrives = express.pick ? delivery?.expressArrives || t.t("expressArrives") : "";

  const token = page?.token ?? "";
  const onPaid = page?.onPaid;
  const paid = useCallback(
    (session: string) => {
      const made = payingNow.current;
      const paidUnits = made ? made.qtys.reduce((sum, n) => sum + n, 0) : 0;
      track("checkout.paid", {
        together: true,
        pieces: made?.lines.length ?? 0,
        ...((made?.previews ?? 1) > 1 ? { previews: made!.previews } : {}),
        // How many in all, where some piece was more than one of it.
        ...(made?.owned && paidUnits > made.lines.length ? { qty: paidUnits } : {}),
        ...(expressArrives ? { express: true } : {}),
      });
      setOutcome("checking");
      setView("paid");
      setPaidArrives(expressArrives);
      onPaid?.("checking", session, expressArrives || undefined);
      const asked = session ? checkoutResult(token, session) : Promise.resolve<CheckoutResult>("unknown");
      void asked.then((result) => {
        const settled = result === "paid" ? "paid" : "processing";
        setOutcome(settled);
        onPaid?.(settled, session, expressArrives || undefined);
      });
    },
    [token, onPaid, expressArrives]
  );
  const fail = useCallback((why: PayFailure) => {
    track("checkout.failed", { why, together: true });
    if (why === "closed") {
      setClosed(true);
      setFailed(null);
      return;
    }
    // One of their other previews is not what the sheet showed any more. The
    // page is reading them again; back to the list, which follows what it
    // reads, for them to look at before paying.
    if (why === "changed") {
      setFailed(null);
      setChanged(true);
      setPaying(null);
      setView("pick");
      return;
    }
    // This page's own price was not the price any more. "Try again" would
    // send the lines frozen in `paying`, with the old price, and be refused
    // for ever; back to the list instead, which the page re-reading itself
    // fills with the new prices, for them to look at before paying.
    if (why === "repriced") {
      setFailed(null);
      setRepriced(true);
      setPaying(null);
      setView("pick");
      return;
    }
    // A base or its message refused: back to the list, where each is chosen,
    // with the page read again if its window has closed.
    if (why === "base") {
      setFailed(null);
      setBaseRefused(true);
      setPaying(null);
      setView("pick");
      return;
    }
    setFailed(why);
  }, []);
  const retry = () => {
    if (failed === "stale") {
      reloadToPay("all");
      return;
    }
    setFailed(null);
    setAttempt((n) => n + 1);
  };

  /** What a piece is called here: its product, and which of its preview's
   *  pieces when that preview has several, in the words its card is headed
   *  with ("Keychain · 1 of 2"), so the sheet does not rename it. */
  function name(kind: LeadKind, at: number, of: number) {
    const product = t.t(KIND_KEY[kind] ?? "kindMinime");
    return of > 1 ? t.t("pieceNamed", { name: product, n: String(at + 1), total: String(of) }) : product;
  }

  if (view === "paid") {
    return (
      <Sheet
        title={t.t(outcome === "checking" ? "wantTitle" : "paidTitle")}
        onClose={onClose}
        closeLabel={t.t("close")}
        footer={
          <button className="btn btn-primary btn-block" onClick={onClose}>
            {t.t("paidDone")}
          </button>
        }
      >
        {outcome === "checking" ? (
          <div aria-busy="true" style={{ padding: "8px 0" }}>
            <div className="spin" style={{ margin: "0 auto" }} />
          </div>
        ) : (
          <p className={outcome === "paid" ? "notice notice-good" : "notice"} style={{ margin: 0 }}>
            {t.t(outcome === "paid" ? "paidThanks" : "paidProcessing")}
          </p>
        )}
        {outcome !== "checking" && (paidArrives || delivery?.arrives) && (
          <p className="small" style={{ margin: "12px 0 0" }}>
            {paidArrives || delivery?.arrives}
          </p>
        )}
        {outcome !== "checking" && card && (
          <p className="small" style={{ margin: "8px 0 0" }}>
            {t.t("paidCard")}
          </p>
        )}
      </Sheet>
    );
  }

  if (view === "pay" && paying) {
    const { chosen, lines, total, vatNote, withCode, owned, qtys, amounts, off } = paying;
    // The multi-buy's percent, for the line about codes, from the first
    // piece that has one.
    const multi = chosen.map((c) => c.offer.multiBuy).find((m) => !!m) ?? null;
    return (
      <Sheet title={t.t("wantTitle")} onClose={onClose} closeLabel={t.t("close")}>
        <ul className="together-summary">
          {chosen.map((c, at) => (
            <li key={keyOf(c)}>
              <span>
                {name(c.kind, c.at, c.of)} · {unbroken(c.size)}
                {owned && (qtys[at] ?? 1) > 1 ? ` ×${qtys[at]}` : ""}
                {c.base && (
                  <span className="tiny muted pay-base">
                    {" · "}
                    {t.t("baseName")}
                    {c.base.message ? ` · ${t.t("baseQuoted", { message: c.base.message })}` : ""}
                  </span>
                )}
              </span>
              <strong className="mono">{owned ? amounts[at] : c.chip.now}</strong>
            </li>
          ))}
          {total && (
            <li className="together-total">
              <span>{t.t("togetherTotal")}</span>
              <strong className="mono">{total}</strong>
            </li>
          )}
        </ul>
        <p className="tiny muted" style={{ margin: "8px 0 12px" }}>
          {/* Two of the same: in Stripe's form, where the sheet does not say
              how many. Where it does, that was said on the list, beside the
              + it points at: this step has no + and the form no quantity, so
              nothing about it here. */}
          {t.t(withCode ? "payNote" : "payNoteFull")}
          {expressNote(t, delivery, page?.language ?? "en")}
          {!owned && <> {t.t("togetherQtyForm")}</>}
          {vatNote && (
            <>
              <br />
              {vatNote}
            </>
          )}
          {/* As in the one-piece sheet: with the extra pieces cheaper,
              Stripe's form has no box for a code. */}
          {owned && off > 0 && !withCode && multi && (
            <>
              <br />
              {t.t("multiBuyNoCodes", offVars(multi.percent))}
            </>
          )}
        </p>
        {closed ? (
          <p className="notice">{t.t("togetherClosed")}</p>
        ) : failed === "ordered" ? (
          <p className="notice notice-good">{t.t("orderedNote")}</p>
        ) : failed === "processing" ? (
          <p className="notice">{t.t("paidProcessing")}</p>
        ) : failed ? (
          <>
            <p className="notice notice-bad">{t.t("togetherFailed")}</p>
            <div className="center">
              <button type="button" className="btn btn-quiet btn-sm" onClick={retry}>
                {t.t(failed === "stale" ? "payReload" : "payRetry")}
              </button>
            </div>
          </>
        ) : (
          <>
          <ExpressChoice ex={express} words={t} delivery={delivery} total={paying.sum} />
          <ExpressHold ex={express}>
          <Suspense fallback={<div className="payform payform-loading" />}>
            <LazyPayForm
              key={`${JSON.stringify(lines)}:${attempt}${express.key ? `:${express.key}` : ""}`}
              lines={lines}
              // Only where the sheet worked the bill out: the server checks
              // it, and charges the lines as the bill has them.
              total={owned ? (total ?? undefined) : undefined}
              express={express.pick}
              onExpressRefused={express.refused}
              onPaid={paid}
              onFailed={fail}
              onLoaded={formShown}
            />
          </Suspense>
          </ExpressHold>
          </>
        )}
        {/* Not beside a payment that is in or on its way: changing the pieces
            would only be paying for them twice. Where the sheet said how
            many, the list is also where that changes (this step has no +),
            and the button says so. */}
        {!closed && failed !== "ordered" && failed !== "processing" && (
          <button
            className="btn btn-ghost btn-block"
            style={{ marginTop: 8 }}
            onClick={() => {
              setFailed(null);
              setPaying(null);
              setView("pick");
            }}
          >
            {t.t(owned ? "togetherChangeQty" : "togetherChange")}
          </button>
        )}
      </Sheet>
    );
  }

  return (
    <Sheet
      title={t.t("togetherTitle")}
      onClose={onClose}
      closeLabel={t.t("close")}
      footer={
        <button
          className="btn btn-primary btn-block btn-lift"
          disabled={!chosen.length || !ownChosen || baseStuck || overCap}
          onClick={() => {
            setFailed(null);
            setChanged(false);
            setRepriced(false);
            setBaseRefused(false);
            setPaying({
              chosen,
              lines,
              total,
              vatNote,
              withCode,
              previews: chosenPreviews.size,
              owned: !!billed,
              qtys: chosen.map(qtyOf),
              amounts: billed ? billed.lines.map((b) => fill(format, b.amount)) : [],
              off: billed?.off ?? 0,
              sum: billed ? { minor: billed.total, format } : minorOf(chosen.map(({ chip, offer }) => ({ cents: chip.cents, format: offer.format }))),
            });
            setView("pay");
          }}
        >
          {!chosen.length
            ? t.t("togetherNone")
            : !ownChosen
              ? t.t("togetherNeedsThis")
              : total
                ? `${t.t("togetherContinue")}\u00a0· ${unbroken(total)}`
                : t.t("togetherContinue")}
        </button>
      }
    >
      {changed && <p className="notice">{t.t("togetherChanged")}</p>}
      {repriced && !changed && <p className="notice">{t.t("repriced")}</p>}
      {baseRefused && !repriced && !changed && <p className="notice">{t.t("baseRefused")}</p>}
      <p className="small muted" style={{ margin: "0 0 12px" }}>
        {t.t("togetherPick")}
        {/* Two of the same, where the sheet says how many: with the + beside
            each piece, on this list, the only step that has one. */}
        {owns && (
          <>
            <br />
            {t.t("togetherQty")}
          </>
        )}
      </p>
      <div className="together-list">
        {pieces.map((p, row) => {
          const { piece, at, of, drawingUrl, token } = p;
          const key = keyOf(p);
          const kind = piece.kind ?? fallbackKind;
          const size = picks[key] ?? null;
          const sizes = piece.price?.sizes ?? [];
          const label = name(kind, at, of);
          // A heading where this page's pieces start and where its others
          // do, only when there are both.
          const heading =
            previews > 1 && (row === 0 || !!pieces[row - 1]?.token !== !!token)
              ? t.t(token ? "togetherOthers" : "togetherThis")
              : null;
          return (
            <div className={size ? "together-piece" : "together-piece together-out"} key={key}>
              {heading && <p className="together-group tiny">{heading}</p>}
              <label className="together-head">
                <input
                  type="checkbox"
                  checked={!!size}
                  disabled={!size && !fits(p)}
                  onChange={(e) => {
                    const on = e.target.checked;
                    choose(p, on ? openingSize(sizes, kind) : null);
                    // In or out, it starts again at one of it.
                    setQtys((before) => {
                      if (!(key in before)) return before;
                      const next = { ...before };
                      delete next[key];
                      return next;
                    });
                    if (!p.token) onQty?.(qtySel(key), 1);
                  }}
                />
                {(drawingUrl || piece.photoUrl) && (
                  <img src={drawingUrl ?? piece.photoUrl ?? ""} alt="" className="together-thumb" />
                )}
                <span className="small">
                  <strong>{label}</strong>
                  {piece.price?.package && <span className="tiny muted"> · {piece.price.package}</span>}
                </span>
              </label>
              {size && (
                <div className="sizepick" role="group" aria-label={`${t.t("paySize")}: ${label}`}>
                  {sizes.map((s) => (
                    <button
                      key={s.size}
                      type="button"
                      aria-pressed={s.size === size}
                      onClick={() => choose(p, s.size)}
                    >
                      <span className="sizepick-size">{s.size}</span>
                      <strong className="mono">{s.now}</strong>
                    </button>
                  ))}
                </div>
              )}
              {/* How many of this one, beside its sizes, where the sheet says
                  how many: + stops where the payment as a whole is full. */}
              {size && owns && (
                <div className="qty-row qty-row-tight">
                  <span className="tiny muted">{t.t("multiBuyQty")}</span>
                  <QtyStepper
                    value={qtyOf(p)}
                    max={qtyOf(p) + Math.max(0, MAX_UNITS - units)}
                    onChange={(n) => count(p, n)}
                    words={t}
                    label={`${t.t("multiBuyQty")} ${label}`}
                  />
                </div>
              )}
              {/* Its Christmas base, for a figure, under its size: the same
                  toggle and field as the sheet of one piece, smaller. */}
              {size && baseLimits && (
                <ChristmasBase
                  compact
                  kind={kind}
                  size={size}
                  sizes={sizes.map((s) => s.size)}
                  choice={bases[qtySel(key)]}
                  onChange={(next) => setBase(p, next)}
                  onSize={(next) => choose(p, next)}
                  drawingUrl={drawingUrl ?? piece.photoUrl ?? null}
                  words={t}
                />
              )}
            </div>
          );
        })}
      </div>
      {/* The bill, from two pieces on: one line a piece in the order they are
          sent, the dearest at its own price and every other one at its
          extra price, and the total the button carries. */}
      {billed && total && billed.units > 1 && (
        <BillBox
          rows={billRows(
            chosen.map((c) => ({
              key: keyOf(c),
              name: name(c.kind, c.at, c.of),
              size: c.size,
              chip: c.chip,
              offer: c.offer,
              withCode: codeOn(c),
              qty: qtyOf(c),
            })),
            billed,
            format,
            t
          )}
          total={total}
          save={billed.off > 0 ? fill(format, billed.off) : null}
          words={t}
        />
      )}
      {overCap && (
        <p className="notice" role="alert" style={{ margin: "12px 0 0" }}>
          {t.t("qtyOver", { n: String(MAX_UNITS) })}
        </p>
      )}
      {atLimit && (
        <p className="tiny muted" style={{ margin: "12px 0 0" }}>
          {t.t("togetherLimit", { n: String(MAX_LINES) })}
        </p>
      )}
      {!atLimit && owns && units >= MAX_UNITS && (
        <p className="tiny muted" style={{ margin: "12px 0 0" }}>
          {t.t("qtyLimit", { n: String(MAX_UNITS) })}
        </p>
      )}
      {notHere > 0 && (
        <p className="tiny muted" style={{ margin: "12px 0 0" }}>
          {t.t("togetherNotHere", { n: String(notHere) })}
        </p>
      )}
      {/* When it arrives and that nothing more is owed, here on the first step
          rather than only after Continue: the total on the button is not the
          whole of the decision without them. The same lines as the details
          card, and the duties line is that one verbatim (owner decision, 9 Oct
          2026: never narrowed or hedged). */}
      <div className="stack" style={{ margin: "12px 0 0", gap: 4 }}>
        {delivery?.arrives && (
          <p className="small" style={{ margin: 0 }}>
            {delivery.arrives}
          </p>
        )}
        {delivery?.christmas && (
          <p className="small" style={{ margin: 0, fontWeight: 600 }}>
            {delivery.christmas}
          </p>
        )}
        <LateLine delivery={delivery} language={page?.language ?? "en"} card={false} style={{ margin: 0 }} />
        <p className="small muted" style={{ margin: 0 }}>
          {t.t("duties")}
        </p>
      </div>
    </Sheet>
  );
}

/** The chips added up, written as the chips are: their own amounts in minor
 *  units, filled into the format the server writes them with. Null unless
 *  every chip carries its amount and all of them one format, which a server
 *  from before it said them does not; then no sum is shown, and Stripe's form
 *  is the only total, as it always is the one that is charged. The filling
 *  is multiBuy.ts's, which the multi-buy's bill writes its total with too. */
function totalOf(chips: { cents?: number; format?: string }[]): string | null {
  const sum = minorOf(chips);
  return sum ? fill(sum.format, sum.minor) : null;
}

/** The same sum in minor units, with the format it is written in. */
function minorOf(chips: { cents?: number; format?: string }[]): ExpressTotal | null {
  if (!chips.length) return null;
  const format = chips[0]!.format;
  if (!format || chips.some((c) => typeof c.cents !== "number" || c.format !== format)) return null;
  return { minor: chips.reduce((sum, c) => sum + (c.cents ?? 0), 0), format };
}

/** Every piece on the page as this sheet offers it, with the drawing each is
 *  shown by: its mockup, or failing that the photo it was drawn from. */
export function togetherPieces(drawings: { piece: PreviewPiece; mockup?: { url: string } }[]): TogetherPiece[] {
  return drawings.map(({ piece, mockup }, at) => ({
    piece,
    at,
    of: drawings.length,
    drawingUrl: mockup?.url ?? null,
    token: "",
  }));
}

/** The pieces of their other previews that this page can take a payment for
 *  (otherPreviews' basket), oldest preview first, as the strip shows them. */
function othersOf(others: OtherPreview[] | null | undefined): TogetherPiece[] {
  return (others ?? []).flatMap((other) => {
    if (other.current || other.ordered || !other.basket?.length) return [];
    const basket = other.basket;
    return basket.map((piece, at) => ({
      piece: { id: piece.id, kind: piece.kind, photoUrl: piece.photoUrl, price: piece.price, pets: piece.pets },
      at,
      of: basket.length,
      drawingUrl: piece.drawingUrl,
      token: other.token,
    }));
  });
}
